import { format } from 'date-fns';
import { InsuranceSubscriberRelationship } from 'domains/client';
import { SUBSCRIBER_RELATIONSHIP_CODE_MAP } from 'domains/signupDirect/businessLogic/relationships';
import {
  EvaluationFormType,
  paymentMethodIsInsurance,
  PrimaryInsuranceSubscriberType,
} from 'domains/signupDirect/schemas/evaluationSchema';
import { checkEligibility, EligibilityCheckPatientParams } from 'domains/signupv4/Insurance/queries';
import { UseFormSetValue } from 'react-hook-form';
import { isNull } from 'lodash';
import { formatPhoneNumber, isPhoneNumberValid } from 'utils/phoneNumber';

type SubmitParams = {
  data: EvaluationFormType;
  setValue: UseFormSetValue<EvaluationFormType>;
  onUpdateEligibilityHasPCP: (hasPCP: boolean) => void;
};

/** For eligibility ONLY */
const getEligibilityCheckSubscriberDetails = (data: EvaluationFormType): Required<EligibilityCheckPatientParams> => {
  if (data.billingInfo.type === 'privatePay') throw `Cant get insurance details if private pay`;

  const result: EligibilityCheckPatientParams = {
    memberID: data.billingInfo.memberId,
  };

  if (
    data.billingInfo.type === 'insurance-commercial' &&
    data.billingInfo.subscriber.relationShipToClient !== InsuranceSubscriberRelationship.Self
  ) {
    result.dateOfBirth = format(data.billingInfo.subscriber.dateOfBirth, 'yyyy-MM-dd');
    result.firstName = data.billingInfo.subscriber.firstName;
    result.lastName = data.billingInfo.subscriber.lastName;
    result.relationshipToClient = SUBSCRIBER_RELATIONSHIP_CODE_MAP[data.billingInfo.subscriber.relationShipToClient];
  } else {
    result.dateOfBirth = format(data.clientInfo.dateOfBirth, 'yyyy-MM-dd');
    result.firstName = data.clientInfo.firstName;
    result.lastName = data.clientInfo.lastName;
    result.relationshipToClient = SUBSCRIBER_RELATIONSHIP_CODE_MAP[InsuranceSubscriberRelationship.Self];
  }

  return result as Required<EligibilityCheckPatientParams>;
};

/** For Salesforce leads */
export const getInsurancePrimarySubscriberDetails = (data: EvaluationFormType): PrimaryInsuranceSubscriberType => {
  if (data.billingInfo.type === 'privatePay') throw `Cant get insurance details if private pay`;

  if (
    data.billingInfo.type === 'insurance-commercial' &&
    data.billingInfo.subscriber.relationShipToClient !== InsuranceSubscriberRelationship.Self
  ) {
    return data.billingInfo.subscriber as PrimaryInsuranceSubscriberType;
  } else {
    return {
      dateOfBirth: data.clientInfo.dateOfBirth,
      firstName: data.clientInfo.firstName,
      lastName: data.clientInfo.lastName,
      relationShipToClient: InsuranceSubscriberRelationship.Self,
    };
  }
};

export const handleCheckEligibility = async ({ data, setValue, onUpdateEligibilityHasPCP }: SubmitParams) => {
  setValue('billingInfo.eligibilityCheckResult', undefined);

  if (!paymentMethodIsInsurance(data.billingInfo)) throw 'Expected billingInfo.type to be insurance';

  const { insuranceData } = data.billingInfo;

  const checkResult = await checkEligibility({
    checkParams: {
      payerID: insuranceData.payerId,
      state: insuranceData.state,
      npi: insuranceData.npiOverride?.toString(),
      payerIDOverride: insuranceData.eligibilityPayerIdOverride?.toString(),
      subscriber: getEligibilityCheckSubscriberDetails(data),
    }, //@TODO(juan) REFACTOR
    insuranceAllowSelfSignUp: insuranceData.allowSelfSignUp,
    insuranceInNetwork: insuranceData.networkStatus,
  });

  // Set the eligibility check result (status)
  setValue('billingInfo.eligibilityCheckResult', checkResult.status);

  // If PCP information is available, populate the primaryPhysician fields
  if (checkResult.pcp && data.billingInfo.insuranceData.insuranceType === 'Medicaid') {
    // Update the context with whether PCP data was found
    onUpdateEligibilityHasPCP(!isNull(checkResult.pcp));
    const { pcp } = checkResult;

    const fullName = [pcp.firstName, pcp.middleName, pcp.name].filter(Boolean).join(' ');

    if (fullName) {
      setValue('primaryPhysician.name', fullName, { shouldValidate: true });
    } else {
      onUpdateEligibilityHasPCP(false);
    }

    if (pcp.phone) {
      const isPhoneValid = isPhoneNumberValid(pcp.phone);
      setValue('primaryPhysician.phone', formatPhoneNumber(pcp.phone), { shouldValidate: true });
      if (!isPhoneValid) {
        onUpdateEligibilityHasPCP(false);
      }
    }

    if (pcp.address) {
      setValue('primaryPhysician.officeName', `${pcp.address.address1}, ${pcp.address.city}, ${pcp.address.state}`, { shouldValidate: true });
    } else {
      onUpdateEligibilityHasPCP(false);
    }

    if (pcp.npi) {
      setValue('primaryPhysician.npi', pcp.npi, { shouldValidate: true });
    }
  }

  return checkResult.status;
};
